import { Injectable } from '@angular/core';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { CommercialOffer, OfferGroup, OfferItem, OfferItemType, DefaultTableType } from '../models/commercial-offer.model';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class CommercialOffersPdfService {
  private pxToPtScaleFactor = 72 / 96;
  private readonly TEST_CLIENT_EMAIL = 'testdemo@builtgenix.com';
  private readonly TEST_CLIENT_LOGO_PATH = 'assets/img/logo-for-testclient.png';

  constructor(private authService: AuthService) { }

  exportOfferToPdf(offer: CommercialOffer, download: boolean = true) {
    const doc = new jsPDF('p', 'pt', 'a4');
    const margin = 40;
    const indent = 40; // Indent for logo, TO, RE, ESTIMATED PRICE
    let y = margin;
    let logoWidth = 0;
    let logoHeight = 0;
    const maxLogoWidth = 200; // Set your desired max width
    const logoX = margin + indent;
    let contentStartY = y;
    let pageNumber = 1;

    const spacingAfterLogo = 30; // Move this up

    // Helper to generate the rest of the PDF after logo is drawn
    const generatePdfContent = (y: number) => {
      let contentStartY = y;
      // TO, RE (indented under logo)
      doc.setFontSize(12);
      doc.setTextColor('#333');
      doc.text(`TO: ${offer.to || ''}`, margin + indent, y);
      y += 18;
      doc.text(`RE: ${offer.re || ''}`, margin + indent, y);
      y += 18;
      contentStartY = y;

      // ESTIMATED PRICE (centered, bold, black)
      const totalValue = this.calculateTotalValue(offer);
      y += 20;
      doc.setFontSize(18);
      doc.setTextColor('#000');
      doc.setFont('helvetica', 'bold');
      doc.text(`ESTIMATED PRICE: $${totalValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, doc.internal.pageSize.width / 2, y, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      y += 18;

      // Breakdown note (centered, small gap)
      doc.setFontSize(10);
      doc.setTextColor('#666');
      doc.setFont('helvetica', 'normal');
      doc.text('Please see below for the breakdown.', doc.internal.pageSize.width / 2, y, { align: 'center' });
      y += 18;

      // Helper: ensure enough space before rendering a block
      function ensureSpace(doc: jsPDF, y: number, needed: number = 60) {
        const pageHeight = doc.internal.pageSize.height;
        if (y + needed > pageHeight - 40) { // 40pt margin for footer
          doc.addPage();
          y = 60; // top margin for new page
        }
        return y;
      }

      // For each group, add a table (only if it has customer items)
      let groupIndex = 0;
      offer.commercialOfferVersions?.[0]?.offerGroups?.forEach((group: OfferGroup) => {
        // Only process groups that have customer items
        if (this.hasCustomerItems(group)) {
          // Add a small vertical gap between tables
          if (groupIndex > 0) {
            y += 18;
          }
          // Ensure space for table heading and table
          y = ensureSpace(doc, y, 100);
          // Table group heading centered (only if not hidden)
          if (!this.shouldHideGroupName(group)) {
            doc.setFontSize(16);
            doc.setTextColor('#333');
            doc.setFont('helvetica', 'bold');
            doc.text(group.name, doc.internal.pageSize.width / 2, y, { align: 'center' });
            doc.setFont('helvetica', 'normal');
            y += 10;
          }

          // Render the group table with proper structure handling
          y = this.renderGroupTable(doc, group, y, margin, indent);
          groupIndex++;
        }
      });

      // Notes
      if (offer.commercialOfferVersions?.[0]?.notes) {
        y += 16;
        y = ensureSpace(doc, y, 40);
        doc.setFontSize(11);
        doc.setTextColor('#555');
        doc.text('Notes:', margin + indent, y);
        y += 15;
        doc.setFontSize(10);
        doc.setTextColor('#666');
        doc.text(offer.commercialOfferVersions?.[0]?.notes, margin + indent, y, { maxWidth: doc.internal.pageSize.width - 2 * (margin + indent) });
        y += 20;
      }

      // Draw footer on the last page
      this.drawFooter(doc);

      // Save or open
      const version = offer.commercialOfferVersions?.[0]?.version || offer.version?.toString() || '1';
      const baseName = offer.name ? offer.name.replace(/\s/g, '_').replace(/[^A-Z0-9_]/gi, '') : 'offer';
      const filename = `${baseName}_v${version}.pdf`;
      if (download) {
        doc.save(filename);
      } else {
        doc.output('dataurlnewwindow', { filename });
      }
    };

    if (offer.logoUrl) {
      const img = new Image();
      const logoSrc = this.isTestClient() ? this.TEST_CLIENT_LOGO_PATH : offer.logoUrl;
      img.src = logoSrc;

      img.onload = () => {
        const naturalWidth = img.naturalWidth;
        const naturalHeight = img.naturalHeight;
        const logoWidth = maxLogoWidth;
        const logoHeight = naturalHeight * (logoWidth / naturalWidth);

        let y = margin;
        try {
          doc.addImage(img, 'PNG', logoX, y, logoWidth, logoHeight);
        } catch (e) { /* ignore image errors */ }

        // --- Add version and date next to logo ---
        if (offer.lastUpdate) {
          const lastUpdateDate = new Date(offer.lastUpdate);
          const formattedDate = lastUpdateDate.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
          }).replace(/\//g, '.');

          doc.setFontSize(10);
          doc.setTextColor('#666');
          doc.setFont('helvetica', 'normal');

          const version = offer.commercialOfferVersions?.[0]?.version || '';
          const lastUpdateX = doc.internal.pageSize.width - margin - indent;

          // Display date on first line
          doc.text(formattedDate, lastUpdateX, y + 20, { align: 'right' });

          // Display version on second line
          if (version) {
            doc.text(`#${version}`, lastUpdateX, y + 32, { align: 'right' });
          }
        }
        // --- End version/date block ---

        y += logoHeight + spacingAfterLogo;
        generatePdfContent(y);
      };
      img.onerror = () => {
        // If image fails to load, just continue without it
        let y = margin + spacingAfterLogo;
        generatePdfContent(y);
      };
    } else {
      let y = margin + spacingAfterLogo;
      generatePdfContent(y);
    }
  }

  private drawFooter(doc: jsPDF) {
    const footerY = doc.internal.pageSize.height - 30;
    const leftX = 40;
    const rightX = doc.internal.pageSize.width - 40;

    // Page number (center)
    doc.setFontSize(8);
    doc.setTextColor('#666');
    doc.text(`${doc.getCurrentPageInfo().pageNumber}`, doc.internal.pageSize.width / 2, footerY, { align: 'center' });

    // Thank you message (right, top)
    doc.setFontSize(8);
    doc.setTextColor('#666');
    doc.text('Thank you for your business!', rightX, footerY - 20, { align: 'right' });

    // Get client info
    const loggedInClient = this.authService.getClientDataStructure();

    // Website and email logic (right, below thank you)
    const showWebSite = loggedInClient?.configuration?.customWebSite !== "";
    const website = showWebSite
      ? (loggedInClient?.configuration?.customWebSite || 'www.builtgenix.com')
      : null;

    const showEmail = loggedInClient?.configuration?.customEmail !== "";
    const email = showEmail
      ? (loggedInClient?.configuration?.customEmail || 'contacts@builtgenix.com')
      : null;

    let rightFooterLine = footerY - 8;
    if (website) {
      doc.setTextColor('#666');
      doc.textWithLink(website, rightX, rightFooterLine, { align: 'right' });
      rightFooterLine += 10;
    }
    if (email) {
      doc.setTextColor('#666');
      doc.textWithLink(email, rightX, rightFooterLine, { align: 'right' });
      rightFooterLine += 10;
    }

    // Logo (left)
    const showLogo = loggedInClient?.configuration?.customLogo !== "";
    if (showLogo) {
      let logoImage = new Image();
      const logoSrc = 'assets/img/pdf-logo.png';
      logoImage.src = logoSrc;
      try {
        let logoImageProperties = doc.getImageProperties(logoImage);
        let logoImageScale = 0.2;
        let logoImageWidth = logoImageProperties.width * this.pxToPtScaleFactor * logoImageScale;
        let logoImageHeight = logoImageProperties.height * this.pxToPtScaleFactor * logoImageScale;
        doc.addImage(
          logoImage,
          logoImageProperties.fileType,
          leftX,
          footerY - logoImageHeight - 5,
          logoImageWidth,
          logoImageHeight,
          'footer-logo',
          'FAST'
        );
      } catch (e) { /* ignore image errors */ }
    }

    // Always show www.builtgenix.com under the logo (left)
    doc.setFontSize(8);
    doc.setTextColor('#666');
    doc.text('www.builtgenix.com', leftX, footerY + 5, { align: 'left' });
  }

  private calculateTotalValue(offer: CommercialOffer): number {
    let total = 0;

    offer.commercialOfferVersions?.[0]?.offerGroups?.forEach((group: OfferGroup) => {
      // Only include groups that have customer items
      if (this.hasCustomerItems(group)) {
        total += this.getGroupSubtotal(group);
      }
    });

    return total;
  }

  private renderGroupTable(doc: jsPDF, group: OfferGroup, y: number, margin: number, indent: number): number {
    // Helper: ensure enough space before rendering a block
    const ensureSpace = (doc: jsPDF, y: number, needed: number = 60) => {
      const pageHeight = doc.internal.pageSize.height;
      if (y + needed > pageHeight - 40) { // 40pt margin for footer
        doc.addPage();
        y = 60; // top margin for new page
      }
      return y;
    };

    // Determine table structure based on group type
    const isMaterialGroup = this.isMaterialGroup(group);
    const hasGroupedSubOfferGroups = this.hasGroupedSubOfferGroups(group);
    const isCustomTable = this.isCustomTable(group);

    // Table columns
    const columns = [
      { header: '#', dataKey: 'number' },
      { header: 'Description', dataKey: 'description' },
      { header: 'Quantity', dataKey: 'quantity' },
    ];

    // Add Unit Price column for material groups and custom tables
    columns.push({ header: 'Unit Price', dataKey: 'unitPrice' });

    // Add Total column
    columns.push({ header: 'Total', dataKey: 'total' });

    // Get flattened items based on group structure
    const flattenedItems = this.getFlattenedGroupItems(group, isMaterialGroup);

    // For material groups, build a flat list of all customer items for continuous numbering
    let allMaterialCustomerItems: any[] = [];
    if (isMaterialGroup) {
      // Helper to flatten all customer items
      const flattenMaterialItems = (group: any): any[] => {
        let items: any[] = [];
        if (group.groupedSubOfferGroups && Array.isArray(group.groupedSubOfferGroups)) {
          group.groupedSubOfferGroups.forEach((groupedSubGroup: any) => {
            if (groupedSubGroup.subOfferGroups && Array.isArray(groupedSubGroup.subOfferGroups)) {
              groupedSubGroup.subOfferGroups.forEach((subGroup: any) => {
                if (subGroup.offerItems && Array.isArray(subGroup.offerItems)) {
                  items.push(...subGroup.offerItems.filter((item: any) => item.type === OfferItemType.CUSTOMER));
                }
              });
            }
          });
        }
        if (group.subOfferGroups && Array.isArray(group.subOfferGroups)) {
          group.subOfferGroups.forEach((subGroup: any) => {
            if (subGroup.offerItems && Array.isArray(subGroup.offerItems)) {
              items.push(...subGroup.offerItems.filter((item: any) => item.type === OfferItemType.CUSTOMER));
            }
          });
        }
        if (group.offerItems && Array.isArray(group.offerItems)) {
          items.push(...group.offerItems.filter((item: any) => item.type === OfferItemType.CUSTOMER));
        }
        return items;
      };
      allMaterialCustomerItems = flattenMaterialItems(group);
    }

    // Prepare table data
    const tableData: any[] = [];
    const rowStyles: any[] = [];

    // Track subgroup for numbering
    let currentSubgroup = null;
    let itemIndex = 1;

    flattenedItems.forEach((item: any) => {
      if (item.isGroupedSubgroupHeader) {
        // Grouped subgroup header row - keep column structure but style as header
        const headerRow = new Array(columns.length).fill('');
        headerRow[1] = item.groupedSubgroupName; // Put the name in the first column
        tableData.push(headerRow);
        rowStyles.push({
          fillColor: '#e8f4fd',
          textColor: '#1976d2',
          fontStyle: 'bold',
          fontSize: 10,
          lineWidth: 1,
          lineColor: '#e0e0e0'
        });
      } else if (item.isSubgroupHeader) {
        // Subgroup header row - spans entire table (solid line)
        currentSubgroup = item.subgroupName;
        itemIndex = 1; // Reset numbering for new subgroup
        const headerRow = new Array(columns.length).fill('');
        headerRow[0] = item.subgroupName; // Put the name in the first column
        tableData.push(headerRow);
        rowStyles.push({
          fillColor: '#ffffff',
          textColor: '#6c757d',
          fontStyle: 'bold',
          fontSize: 10,
          lineWidth: 1,
          lineColor: '#e0e0e0',
          colSpan: columns.length // Span all columns for solid line
        });
      } else if (item.isGroupedSubgroupSubtotal) {
        // Grouped subgroup subtotal row
        const subtotalRow = columns.map(col => {
          if (col.dataKey === 'number') return '';
          if (col.dataKey === 'description') return 'SUBTOTAL';
          else if (col.dataKey === 'total') return `$${item.totalPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          return '';
        });
        tableData.push(subtotalRow);
        rowStyles.push({
          fillColor: '#ffffff',
          textColor: '#2e7d32',
          fontStyle: 'bold',
          fontSize: 9,
          lineWidth: 1,
          lineColor: '#e0e0e0'
        });
      } else if (item.isSubgroupSubtotal) {
        // Subgroup subtotal row
        const subtotalRow = columns.map(col => {
          if (col.dataKey === 'number') return '';
          if (col.dataKey === 'description') return 'SUBTOTAL';
          else if (col.dataKey === 'total') return `$${item.totalPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          return '';
        });
        tableData.push(subtotalRow);
        rowStyles.push({
          fillColor: '#ffffff',
          textColor: '#2e7d32',
          fontStyle: 'bold',
          fontSize: 8,
          lineWidth: 1,
          lineColor: '#e0e0e0'
        });
      } else {
        // Regular item row
        let numberStr = itemIndex.toString();
        if (isMaterialGroup && allMaterialCustomerItems.length > 0) {
          // Find the index of this item in the flat list by id or composite key
          let idx = -1;
          if (item.id) {
            idx = allMaterialCustomerItems.findIndex((i: any) => i.id === item.id);
          } else {
            idx = allMaterialCustomerItems.findIndex((i: any) =>
              i.designation === item.designation &&
              i.description === item.description &&
              i.quantity === item.quantity &&
              i.unitPrice === item.unitPrice
            );
          }
          numberStr = idx >= 0 ? (idx + 1).toString() : itemIndex.toString();
        }
        const row: { [key: string]: string } = {
          number: numberStr,
          description: item.description,
          quantity: item.quantity + (item.quantityDescription ? ' ' + item.quantityDescription : ''),
          unitPrice: item.unitPrice ? `$${item.unitPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '',
          total: item.totalPrice ? `$${item.totalPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '',
        };

        const dataRow = columns.map(col => row[col.dataKey] || '');
        tableData.push(dataRow);
        rowStyles.push({}); // Default styling for regular rows
        itemIndex++;
      }
    });

    // Add group total row
    const groupTotal = this.getGroupTotal(group);
    const totalRow = columns.map(col => {
      if (col.dataKey === 'number') return '';
      if (col.dataKey === 'description') return 'TOTAL';
      else if (col.dataKey === 'total') return `$${groupTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      return '';
    });
    tableData.push(totalRow);
    rowStyles.push({
      fillColor: '#e8f5e9',
      textColor: '#2e7d32',
      fontStyle: 'bold',
      fontSize: 11,
      lineWidth: 1,
      lineColor: '#e0e0e0'
    });

    // Add table with borders and styling
    (autoTable as any)(doc, {
      startY: y,
      head: [columns.map(c => c.header)],
      body: tableData,
      styles: {
        fontSize: 9,
        valign: 'middle',
        textColor: '#333',
        lineWidth: 0.5,
        lineColor: '#e0e0e0',
        cellPadding: 6,
      },
      headStyles: {
        fillColor: '#f8f9fa',
        textColor: '#495057',
        fontStyle: 'bold',
        fontSize: 10,
        lineWidth: 1,
        lineColor: '#e0e0e0',
      },
      bodyStyles: {
        lineWidth: 0.5,
        lineColor: '#e0e0e0',
      },
      theme: 'grid',
      margin: { left: margin + indent, right: margin, bottom: 80 },
      tableWidth: 'auto',
      columnStyles: {
        0: { cellWidth: 30 },  // Numbering column
        1: { cellWidth: 'auto' },  // Description column (auto width)
        2: { cellWidth: 80 },  // Quantity column
        3: { cellWidth: 60 },   // Unit Price column (if exists)
        4: { cellWidth: 70 }    // Total/Price column
      },
      didDrawPage: (data: any) => {
        y = data.cursor.y + 10;
        this.drawFooter(doc);
      },
      didParseCell: (data: any) => {
        // Apply custom row styling
        if (data.row.index < rowStyles.length) {
          const style = rowStyles[data.row.index];
          if (style.fillColor) data.cell.styles.fillColor = style.fillColor;
          if (style.textColor) data.cell.styles.textColor = style.textColor;
          if (style.fontStyle) data.cell.styles.fontStyle = style.fontStyle;
          if (style.fontSize) data.cell.styles.fontSize = style.fontSize;
          if (style.lineWidth) data.cell.styles.lineWidth = style.lineWidth;
          if (style.lineColor) data.cell.styles.lineColor = style.lineColor;

          // Handle column spanning for SubOfferGroup headers (solid line)
          if (style.colSpan && data.column.index === 0) {
            data.cell.colSpan = style.colSpan;
            data.cell.styles.halign = 'left';
          }
          // Center align GroupedSubOfferGroup header text in the first column (with column divisions)
          else if (style.fillColor === '#e8f4fd' && data.column.index === 0) {
            data.cell.styles.halign = 'center';
          }
        }

        // Style the last column (total/price) as bold for regular items
        if (data.column.index === data.table.columns.length - 1 &&
            data.row.index < flattenedItems.length &&
            !flattenedItems[data.row.index].isSubgroupHeader &&
            !flattenedItems[data.row.index].isGroupedSubgroupHeader &&
            !flattenedItems[data.row.index].isSubgroupSubtotal &&
            !flattenedItems[data.row.index].isGroupedSubgroupSubtotal) {
          data.cell.styles.fontStyle = 'bold';
        }

        // Style unit price column with right alignment
        if (data.column.index === 3) {
          data.cell.styles.halign = 'right';
        }

        // Style total/price column with right alignment
        if (data.column.index === data.table.columns.length - 1) {
          data.cell.styles.halign = 'right';
        }
      }
    });

    y += 20;
    return y;
  }

  private isMaterialGroup(group: OfferGroup): boolean {
    return group.name.toLowerCase().includes('material') ||
           (group.groupedSubOfferGroups && group.groupedSubOfferGroups.some((groupedSubGroup: any) =>
             groupedSubGroup.subOfferGroups.some((subGroup: any) =>
               subGroup.offerItems.some((item: any) => item.tableType === DefaultTableType.MATERIALS)
             )
           ));
  }

  private isCustomTable(group: OfferGroup): boolean {
    return !this.isMaterialGroup(group) && this.hasGroupedSubOfferGroups(group);
  }

  private hasGroupedSubOfferGroups(group: OfferGroup): boolean {
    return group.groupedSubOfferGroups && Array.isArray(group.groupedSubOfferGroups) &&
           group.groupedSubOfferGroups.length > 0;
  }

  private getFlattenedGroupItems(group: OfferGroup, isMaterialGroup: boolean): any[] {
    const flattenedItems: any[] = [];

    // Handle groupedSubOfferGroups structure
    if (group.groupedSubOfferGroups && Array.isArray(group.groupedSubOfferGroups)) {
      group.groupedSubOfferGroups.forEach((groupedSubGroup: any) => {
        if (groupedSubGroup.subOfferGroups && Array.isArray(groupedSubGroup.subOfferGroups)) {
          // Check if this grouped subgroup has any customer items
          const hasCustomerItems = groupedSubGroup.subOfferGroups.some((subGroup: any) => {
            return subGroup.offerItems && Array.isArray(subGroup.offerItems) &&
                   subGroup.offerItems.some((item: any) => item.type === OfferItemType.CUSTOMER);
          });

          if (hasCustomerItems) {
            // Add grouped subgroup header (only for material group and if not hidden)
            if (isMaterialGroup && !this.shouldHideSubgroupName(groupedSubGroup)) {
              flattenedItems.push({
                isGroupedSubgroupHeader: true,
                groupedSubgroupName: groupedSubGroup.name,
                designation: '',
                description: '',
                quantity: '',
                unitPrice: 0,
                totalPrice: 0
              });
            }

            // Process each subgroup within the grouped subgroup
            groupedSubGroup.subOfferGroups.forEach((subGroup: any) => {
              const customerItems = this.getCustomerItems(subGroup);

              if (customerItems.length > 0) {
                // Add subgroup header (only for material group and if not hidden)
                if (isMaterialGroup && !this.shouldHideSubgroupName(subGroup)) {
                  flattenedItems.push({
                    isSubgroupHeader: true,
                    subgroupName: subGroup.name,
                    groupedSubgroupName: groupedSubGroup.name,
                    designation: '',
                    description: '',
                    quantity: '',
                    unitPrice: 0,
                    totalPrice: 0
                  });
                }

                // Add customer items
                customerItems.forEach((item: any) => {
                  if (isMaterialGroup) {
                    flattenedItems.push({
                      ...item,
                      subgroupName: subGroup.name,
                      groupedSubgroupName: groupedSubGroup.name
                    });
                  } else {
                    flattenedItems.push({ ...item });
                  }
                });
              }
            });

            // Add grouped subgroup subtotal (only for material group and if not hidden)
            if (isMaterialGroup && !this.shouldHideSubgroupName(groupedSubGroup)) {
              flattenedItems.push({
                isGroupedSubgroupSubtotal: true,
                groupedSubgroupName: groupedSubGroup.name,
                designation: '',
                description: '',
                quantity: '',
                unitPrice: 0,
                totalPrice: this.getGroupedSubgroupSubtotal(groupedSubGroup)
              });
            }
          }
        }
      });
    }

    // Note: Legacy subOfferGroups and offerItems structures are not supported in the current OfferGroup interface
    // The structure now only supports groupedSubOfferGroups

    return flattenedItems;
  }

  private getCustomerItems(subGroup: any): any[] {
    if (!subGroup || !Array.isArray(subGroup.offerItems)) {
      return [];
    }
    return subGroup.offerItems.filter((item: any) => item.type === OfferItemType.CUSTOMER);
  }

  private getGroupSubtotal(group: OfferGroup): number {
    let total = 0;

    // Calculate subtotal from groupedSubOfferGroups
    if (group.groupedSubOfferGroups && Array.isArray(group.groupedSubOfferGroups)) {
      total += group.groupedSubOfferGroups.reduce((subTotal: number, groupedSubGroup: any) => {
        return subTotal + this.getGroupedSubgroupSubtotal(groupedSubGroup);
      }, 0);
    }

    return total;
  }

  private getGroupTotal(group: OfferGroup): number {
    return this.getGroupSubtotal(group);
  }

  private getSubGroupSubtotal(subGroup: any): number {
    if (!Array.isArray(subGroup.offerItems)) {
      return 0;
    }
    return subGroup.offerItems
      .filter((item: any) => item.type === OfferItemType.CUSTOMER)
      .reduce((total: number, item: any) => {
        return total + (item.totalPrice || item.total || 0);
      }, 0);
  }

  private getGroupedSubgroupSubtotal(groupedSubGroup: any): number {
    if (!groupedSubGroup || !Array.isArray(groupedSubGroup.subOfferGroups)) {
      return 0;
    }

    return groupedSubGroup.subOfferGroups.reduce((total: number, subGroup: any) => {
      return total + this.getSubGroupSubtotal(subGroup);
    }, 0);
  }

  private isTestClient(): boolean {
    const currentUser = this.authService.getCurrentUser();
    // Since getCurrentUser returns an Observable, we need to handle this synchronously
    // We'll use the stored user data from localStorage as a fallback
    const storedUser = localStorage.getItem('loggedInUser');
    if (storedUser) {
      const user = JSON.parse(storedUser);
      return user?.userName === this.TEST_CLIENT_EMAIL;
    }
    return false;
  }

  private getClientLogoUrl(): string {
    if (this.isTestClient()) {
      return this.TEST_CLIENT_LOGO_PATH;
    }

    const loggedInClient = this.authService.getClientDataStructure();
    return loggedInClient?.configuration?.customLogo || 'assets/img/pdf-logo.png';
  }

  private hasCustomerItems(group: OfferGroup): boolean {
    if (!group) {
      return false;
    }

    // Check if there are any customer items in the group
    const customerItems = this.getAllCustomerItemsFlat(group);
    return customerItems.length > 0;
  }

  private getAllCustomerItemsFlat(group: OfferGroup): any[] {
    let items: any[] = [];

    // From groupedSubOfferGroups
    if (group.groupedSubOfferGroups && Array.isArray(group.groupedSubOfferGroups)) {
      group.groupedSubOfferGroups.forEach((groupedSubGroup: any) => {
        if (groupedSubGroup.subOfferGroups && Array.isArray(groupedSubGroup.subOfferGroups)) {
          groupedSubGroup.subOfferGroups.forEach((subGroup: any) => {
            if (subGroup.offerItems && Array.isArray(subGroup.offerItems)) {
              items.push(...subGroup.offerItems.filter((item: any) => item.type === OfferItemType.CUSTOMER));
            }
          });
        }
      });
    }

    return items;
  }

  private shouldHideGroupName(group: OfferGroup): boolean {
    if (!group || !group.name) {
      return false;
    }

    // For custom tables (non-material groups), hide default category names
    if (!this.isMaterialGroup(group)) {
      const name = group.name.toLowerCase().trim();
      return name === 'no name' ||
             name === 'default' ||
             name === 'unnamed' ||
             name === '' ||
             name === 'no nane' ||
             name === 'untitled';
    }

    return false;
  }

  private shouldHideSubgroupName(subgroup: any): boolean {
    if (!subgroup || !subgroup.name) {
      return false;
    }

    const name = subgroup.name.toLowerCase().trim();
    return name === 'no name' ||
           name === 'default' ||
           name === 'unnamed' ||
           name === '' ||
           name === 'no nane' ||
           name === 'untitled';
  }
}
